import { createRoot } from 'react-dom/client';

/**
 * Every root this module created and still owns: element -> { root, Component, wrapper, props,
 * requireDataProps }. It lets discovery skip an element that is already mounted (or lives inside a
 * mounted root) and lets a caller unmount one root explicitly. See docs/PLAN-SHOPPER-ADD-TO-CART.md
 * "Embedded purchase-form bridge".
 */
const rootRegistry = new Map();
/** Records of roots that were unmounted on purpose, kept so `remountComponent` can restore them. */
const retiredRoots = new WeakMap();

/** Subtrees under this attribute are never (re)discovered until the owner removes the marker. */
export const RETIRED_MOUNT_ATTRIBUTE = 'data-s-mount-retired';

function resolveElements(selector) {
  if (selector.startsWith('#')) {
    const element = document.getElementById(selector.slice(1));
    return element ? [element] : [];
  }
  if (selector.startsWith('.')) {
    return Array.from(document.getElementsByClassName(selector.slice(1)));
  }

  const element = document.getElementById(selector);
  return element
    ? [element]
    : Array.from(document.getElementsByClassName(selector));
}

/**
 * Collect the current values of any named form controls already inside a mount
 * element, keyed by the control's `name`. `createRoot().render()` replaces the
 * element's children, so server-rendered inputs that act as the no-JS fallback
 * (and anything a shopper typed into them before this bundle executed) would
 * otherwise be discarded on mount. Components opt in by consuming the resulting
 * `domValues` prop and preferring it, per field, over `initialValue`.
 *
 * Unchecked checkboxes and radios are omitted (an unchecked box submits
 * nothing); every other control contributes its `.value`, empty string included,
 * so a field a shopper deliberately cleared stays cleared.
 */
export function collectDomValues(element) {
  const values = {};
  const controls = element.querySelectorAll(
    'input[name], select[name], textarea[name]',
  );
  controls.forEach(control => {
    const { name, type } = control;
    if (!name) return;
    if ((type === 'checkbox' || type === 'radio') && !control.checked) return;
    values[name] = control.value;
  });
  return values;
}

/** An injection point is a Component, or `{ Component, requireDataProps }`. */
function normalizeInjectionPoint(value) {
  if (value && typeof value === 'object' && 'Component' in value) {
    return { Component: value.Component, requireDataProps: value.requireDataProps === true };
  }
  return { Component: value, requireDataProps: false };
}

function isInsideRegisteredRoot(element) {
  for (const registered of rootRegistry.keys()) {
    if (registered === element || registered.contains(element)) return true;
  }
  return false;
}

function parseProps(element, selector) {
  if (!element.hasAttribute('data-props')) return {};
  const rawProps = element.getAttribute('data-props');
  try {
    return JSON.parse(rawProps);
  } catch (error) {
    console.error(
      `Failed to parse data-props for ${selector}:`,
      error,
      rawProps,
    );
    return null;
  }
}

function renderRoot(element, Component, wrapper, props, requireDataProps) {
  const rendered = { ...props };
  const domValues = collectDomValues(element);
  if (Object.keys(domValues).length > 0 && rendered.domValues === undefined) {
    rendered.domValues = domValues;
  }

  const root = createRoot(element);
  rootRegistry.set(element, { root, Component, wrapper, props, requireDataProps });
  root.render(wrapper(Component, rendered, element));
}

/**
 * Mounts each injection point's React component on every matching element.
 *
 * Candidates are collected for every selector before any `createRoot` runs, so a component that
 * renders markup matching another selector cannot be discovered by the same pass. An element is
 * skipped when it is already a registered root, sits inside one, or sits inside a
 * `[data-s-mount-retired]` subtree. An injection point may be `{ Component, requireDataProps: true }`
 * to mount only elements that carry `data-props` (CSS classes alone are not mount markers for the
 * shopper booking points). `wrapper(Component, props, element)` receives the mount element.
 */
export default function mountComponents(injectionPoints, wrapper) {
  // Forget roots whose container left the document (SPA navigation); they cannot be rediscovered.
  for (const registered of rootRegistry.keys()) {
    if (!registered.isConnected) rootRegistry.delete(registered);
  }

  const candidates = [];
  Object.entries(injectionPoints).forEach(([selector, value]) => {
    const { Component, requireDataProps } = normalizeInjectionPoint(value);
    resolveElements(selector).forEach(element => {
      candidates.push({ selector, element, Component, requireDataProps });
    });
  });

  candidates.forEach(({ selector, element, Component, requireDataProps }) => {
    if (requireDataProps && !element.hasAttribute('data-props')) return;
    if (isInsideRegisteredRoot(element)) return;
    if (element.closest(`[${RETIRED_MOUNT_ATTRIBUTE}]`)) return;

    const props = parseProps(element, selector);
    if (props === null) return;

    renderRoot(element, Component, wrapper, props, requireDataProps);
  });
}

/** Whether `element` is a root created by `mountComponents` and not yet unmounted. */
export function isMountedComponent(element) {
  return rootRegistry.has(element);
}

/**
 * Unmounts the root on `element`, forgets it, and keeps its record for `remountComponent`.
 * Returns false when `element` is not a registered root. React's own `root.unmount()` empties the
 * container, so callers that need the contents back must remount.
 */
export function unmountComponent(element) {
  const record = rootRegistry.get(element);
  if (!record) return false;
  rootRegistry.delete(element);
  record.root.unmount();
  retiredRoots.set(element, record);
  return true;
}

/**
 * Re-mounts an element previously unmounted with `unmountComponent`, with its original props.
 * Returns false when the element was never retired this way or is already mounted.
 */
export function remountComponent(element) {
  const record = retiredRoots.get(element);
  if (!record || rootRegistry.has(element)) return false;
  retiredRoots.delete(element);
  renderRoot(element, record.Component, record.wrapper, record.props, record.requireDataProps);
  return true;
}

/**
 * Unmounts every root whose element sits inside `container` (or is it). Callers that clear or
 * discard injected markup, such as the Library modal, use this so a detached root stops running
 * its timers and requests. Returns the number of roots unmounted.
 */
export function unmountComponentsWithin(container) {
  if (!container) return 0;
  const inside = Array.from(rootRegistry.keys())
    .filter(element => element === container || container.contains(element));
  inside.forEach(element => unmountComponent(element));
  return inside.length;
}
